import { useCallback, useEffect, useState } from 'react'

// Per-case saved locus list (GEN-3103), TGG-style. Persisted in localStorage so
// it survives refreshes in this browser, but is intentionally NOT in the URL
// (matching TGG: a shared/bookmarked link won't carry someone else's list).
const KEY_PREFIX = 'vlens:locusList:'

function read(caseId: string): string[] {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + caseId)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

export type LocusList = {
  items: string[]
  addMany: (entries: string[]) => void
  remove: (entry: string) => void
  clear: () => void
}

export function useLocusList(caseId: string): LocusList {
  const [items, setItems] = useState<string[]>(() => read(caseId))

  // Reload when the case changes (e.g. navigating between cases).
  useEffect(() => {
    setItems(read(caseId))
  }, [caseId])

  // Persist inside the functional update so we always write the freshest value.
  const setAndPersist = useCallback(
    (updater: (prev: string[]) => string[]) => {
      setItems((prev) => {
        const next = updater(prev)
        try {
          localStorage.setItem(KEY_PREFIX + caseId, JSON.stringify(next))
        } catch {
          // storage unavailable / over quota: keep the in-memory copy.
        }
        return next
      })
    },
    [caseId]
  )

  const addMany = useCallback(
    (entries: string[]) =>
      setAndPersist((prev) => {
        const merged = [...prev]
        for (const raw of entries) {
          const entry = raw.trim()
          if (entry && !merged.includes(entry)) merged.push(entry)
        }
        return merged
      }),
    [setAndPersist]
  )

  const remove = useCallback(
    (entry: string) => setAndPersist((prev) => prev.filter((e) => e !== entry)),
    [setAndPersist]
  )

  const clear = useCallback(() => setAndPersist(() => []), [setAndPersist])

  return { items, addMany, remove, clear }
}
